/**
 * colorUtils.ts - Color Manipulation and Analysis Utilities
 *
 * This utility module provides functions for color manipulation, analysis, and theming support.
 * Used throughout the application for dynamic color generation and accessibility features.
 *
 * Key Functions:
 * - isLightColor: Determines if a color is light or dark for contrast decisions
 * - darkenColor: Creates darker variants of colors for hover states and shadows
 * - lightenColor: Creates lighter variants for backgrounds and subtle elements
 *
 * Features:
 * - Hex color format support
 * - Brightness calculation using luminance formula
 * - Percentage-based color adjustments
 * - Safe fallbacks for invalid color inputs
 *
 * Dependencies: None (pure JavaScript/TypeScript)
 */

// Utility functions for color manipulation and analysis

/**
 * Determines if a hex color is light or dark based on its brightness
 * @param hexColor - Hex color string (e.g., "#ffffff")
 * @returns true if the color is light, false if dark
 */
export const isLightColor = (hexColor: string): boolean => {
  if (!hexColor || !hexColor.startsWith("#")) return false;
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155;
};

/**
 * Darkens a hex color by a specified percentage
 * @param hexColor - Hex color string (e.g., "#ffffff")
 * @param percent - Percentage to darken (default: 40)
 * @returns Darkened hex color string
 */
export const darkenColor = (hexColor: string, percent: number = 40): string => {
  if (!hexColor || !hexColor.startsWith("#")) return hexColor;
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  const darkenedR = Math.max(0, Math.floor(r * (1 - percent / 100)));
  const darkenedG = Math.max(0, Math.floor(g * (1 - percent / 100)));
  const darkenedB = Math.max(0, Math.floor(b * (1 - percent / 100)));

  return `#${darkenedR.toString(16).padStart(2, "0")}${darkenedG.toString(16).padStart(2, "0")}${darkenedB.toString(16).padStart(2, "0")}`;
};

/**
 * Lightens a hex color by a specified percentage
 * @param hex - Hex color string (e.g., "#ffffff")
 * @param percent - Percentage to lighten (default: 25)
 * @returns Lightened hex color string
 */
export const lightenColor = (hex: string, percent: number = 25): string => {
  if (!hex || !hex.startsWith("#")) return hex;

  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

export const withAlpha = (color: string, alpha: number) => {
  if (color.startsWith("#")) {
    return `${color}${Math.round(alpha * 255)
      .toString(16)
      .padStart(2, "0")}`;
  }

  if (color.startsWith("rgba")) {
    return color.replace(
      /rgba\(([^,]+),([^,]+),([^,]+),([^)]+)\)/,
      `rgba($1,$2,$3,${alpha})`
    );
  }

  if (color.startsWith("rgb")) {
    return color.replace(
      /rgb\(([^,]+),([^,]+),([^)]+)\)/,
      `rgba($1,$2,$3,${alpha})`
    );
  }

  return color;
};
